import type { MemberEnrollment } from '@/services/enrollments.service';
import type { AreaAccess, MemberArea } from '@/services/member-areas.service';

export function isPlatformAdmin(role?: string | null): boolean {
  return role === 'ADMIN' || role === 'SUPER_ADMIN';
}

export function hasCurrentEnrollment(
  enrollment: MemberEnrollment,
  now = Date.now(),
): boolean {
  return (
    enrollment.status === 'ACTIVE' &&
    (!enrollment.expirationDate ||
      new Date(enrollment.expirationDate).getTime() > now)
  );
}

export function availableHomeCourses(
  enrollments: MemberEnrollment[],
  now = Date.now(),
): MemberEnrollment[] {
  const products = new Map<string, MemberEnrollment>();
  for (const enrollment of enrollments) {
    if (
      !hasCurrentEnrollment(enrollment, now) ||
      !enrollment.product?.slug ||
      enrollment.product.status !== 'PUBLISHED'
    )
      continue;
    const previous = products.get(enrollment.product.id);
    if (!previous || enrollment.progress > previous.progress)
      products.set(enrollment.product.id, enrollment);
  }
  return [...products.values()];
}

/** Matrículas da conta: liberadas primeiro, depois vencidas, sem repetir. */
export function homeCourses(
  enrollments: MemberEnrollment[],
  now = Date.now(),
): MemberEnrollment[] {
  const active = availableHomeCourses(enrollments, now);
  const shown = new Set(active.map((enrollment) => enrollment.product!.id));
  const expired: MemberEnrollment[] = [];
  for (const enrollment of enrollments) {
    const product = enrollment.product;
    if (
      enrollment.status !== 'EXPIRED' ||
      product?.access?.reason !== 'expired' ||
      !product.slug ||
      product.status !== 'PUBLISHED' ||
      shown.has(product.id)
    )
      continue;
    shown.add(product.id);
    expired.push(enrollment);
  }
  return [...active, ...expired];
}

// Aliases choose artwork and filtering only. Access comes from the authenticated
// member-area query: enrolled areas for students, admin endpoints for admins.
export function ecosystemKey(
  slug: string,
): 'devclub' | 'iaclub' | 'works' | undefined {
  const key = slug.toLowerCase().replace(/[\s._-]/g, '');
  if (['devclub', 'devcourse'].includes(key)) return 'devclub';
  if (key === 'iaclub') return 'iaclub';
  if (['worksai', 'works'].includes(key)) return 'works';
  return undefined;
}

/**
 * Formações liberadas do produto. O aluno recebe a decisão por formação; no
 * admin entram as do produto e as dos grupos ligados a ele.
 */
export function areaProductIds(
  area: MemberArea,
  adminPreview = false,
): Set<string> {
  if (adminPreview && Array.isArray(area.courses))
    return new Set(area.courses.map(course => course.id));
  if (!adminPreview && area.access)
    return new Set(
      (area.courses ?? [])
        .filter((course) => course.access?.allowed)
        .map((course) => course.id),
    );
  return new Set([
    ...(adminPreview ? (area.courses ?? []).map((course) => course.id) : []),
    ...(area.accessGroups
      ?.filter((group) => adminPreview || group.isActive)
      .flatMap(
        (group) =>
          group.products
            ?.filter(
              (product) => adminPreview || product.status === 'PUBLISHED',
            )
            .map((product) => product.id) ?? [],
      ) ?? []),
  ]);
}

/** Todas as formações do produto, inclusive as com cadeado (catálogo da área). */
export function areaCourseIds(
  area: MemberArea,
  adminPreview = false,
): Set<string> {
  if (!adminPreview && area.access)
    return new Set((area.courses ?? []).map((course) => course.id));
  return areaProductIds(area, adminPreview);
}

const notIncluded: AreaAccess = {
  allowed: false,
  reason: 'not_owned',
  expiresAt: null,
};

/** Acesso ao produto; resposta antiga (sem `access`) libera quando há formação liberada. */
export function areaAccess(area: MemberArea, adminPreview = false): AreaAccess {
  if (adminPreview)
    return { allowed: true, reason: 'granted', expiresAt: null };
  if (area.access) return area.access;
  return areaProductIds(area).size > 0
    ? { allowed: true, reason: 'granted', expiresAt: null }
    : notIncluded;
}

function latest(dates: Array<string | null>): string | null {
  return dates.reduce<string | null>(
    (max, date) => (date && (!max || date > max) ? date : max),
    null,
  );
}

/** Várias áreas da mesma marca: liberado vence expirado, que vence não incluído. */
export function combinedAccess(accesses: AreaAccess[]): AreaAccess {
  const allowed = accesses.filter((access) => access.allowed);
  if (allowed.length)
    return {
      allowed: true,
      reason: 'granted',
      expiresAt: allowed.some((access) => !access.expiresAt)
        ? null
        : latest(allowed.map((access) => access.expiresAt)),
    };
  const expired = accesses.filter((access) => access.reason === 'expired');
  if (expired.length)
    return {
      allowed: false,
      reason: 'expired',
      expiresAt: latest(expired.map((access) => access.expiresAt)),
    };
  return notIncluded;
}

/** Produto principal: o mais alto da hierarquia que o aluno pode abrir (marcado pela API). */
export function mainArea(areas: MemberArea[]): MemberArea | undefined {
  return areas.find((area) => area.isMain && area.access?.allowed);
}

export type HeroMode = 'main' | 'expired' | 'admin';

/**
 * Produto do banner principal da home. Aluno: o principal; sem nenhum ativo,
 * o mais alto que venceu, para renovar. Admin: o topo da hierarquia, como
 * prévia do que vê um aluno que tem tudo.
 */
export function heroArea(
  areas: MemberArea[],
  adminPreview = false,
): { area: MemberArea; mode: HeroMode } | undefined {
  // sort estável: empates mantêm a ordem da API (prioridade, depois criação)
  const ordered = [...areas].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0),
  );
  if (adminPreview) {
    const top = ordered.find((area) => area.isActive) ?? ordered[0];
    return top && { area: top, mode: 'admin' };
  }
  const main = mainArea(ordered);
  if (main) return { area: main, mode: 'main' };
  const expired = ordered.find((area) => area.access?.reason === 'expired');
  return expired && { area: expired, mode: 'expired' };
}

/**
 * Página de renovação de um produto vencido: a primeira que as formações dele
 * trazem (link direto ou checkout de vídeo, como no diálogo do cadeado).
 */
export function areaRenewUrl(area: MemberArea): string | null {
  for (const course of area.courses ?? []) {
    const action =
      course.access?.reason === 'expired' ? course.access.action : undefined;
    if (action?.type === 'url') return action.url;
    if (action?.type === 'video' && action.checkoutUrl)
      return action.checkoutUrl;
  }
  return null;
}

export function progressPercent(value: number): number {
  return Number.isFinite(value)
    ? Math.round(Math.max(0, Math.min(100, value)))
    : 0;
}
